import parse from 'html-react-parser';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import {
  IeltsListeningUserAttemptQuery,
  IeltsReadingUserAttemptQuery,
  IeltsSkill,
  IeltsWritingUserAttemptQuery,
} from '@/__generated__/graphql';
import IeltsInput from '@/components/ielts/common/Inputs/TextInput';
import { activeListeningPartAtom, activeReadingPartAtom } from '@/store/ieltsListeningReading';
import { IeltsListeningReadingWritingFormValues, IeltsListeningReadingWritingQuestion } from '@/types';
import useIeltsListening from '../../../../listening/CheckPoint/DoExam/useIeltsListening';
import { configQuestionContent } from '../config';
import useIeltsReading from '@/components/ielts/reading/CheckPoint/DoExam/useIeltsReading';
import { ieltsWritingCurrentPartAtom } from '@/store/ieltsWriting';
import useDoIeltsWriting from '@/components/ielts/writing/CheckPoint/useDoIeltsWriting';
import useIeltsTheme from '../../../hooks/useIeltsTheme';

type Props = {
  question: IeltsListeningReadingWritingQuestion;
  isViewReport?: boolean;
  questionAttempt?: NonNullable<
    | NonNullable<IeltsListeningUserAttemptQuery['userAttempt']>['ieltsListeningPartAttempts']
    | NonNullable<IeltsReadingUserAttemptQuery['userAttempt']>['ieltsReadingPartAttempts']
    | NonNullable<IeltsWritingUserAttemptQuery['userAttempt']>['ieltsWritingPartAttempts']
  >[number]['questionAttempts'][number];
  type: IeltsSkill.Listening | IeltsSkill.Reading | IeltsSkill.Writing;
};

export default function IeltsListeningReadingGapFill({ question, isViewReport, questionAttempt, type }: Props) {
  const { t } = useTranslation('ielts');
  const { theme } = useIeltsTheme();
  const { watch } = useFormContext<IeltsListeningReadingWritingFormValues>() || {};

  const details = useMemo(() => question.node.details, [question.node.details]);

  const activeListeningPart = useAtomValue(activeListeningPartAtom);
  const activeReadingPart = useAtomValue(activeReadingPartAtom);
  const activeWritingPart = useAtomValue(ieltsWritingCurrentPartAtom);
  const activePart = type === IeltsSkill.Listening ? activeListeningPart : type === IeltsSkill.Writing ? activeWritingPart : activeReadingPart;
  const partQuestions =
    (type === IeltsSkill.Listening
      ? activeListeningPart?.node.ieltsListeningPartQuestions
      : type === IeltsSkill.Writing
        ? activeWritingPart?.node.ieltsWritingPartQuestions
        : activeReadingPart?.node.ieltsReadingPartQuestions) || [];
  const questionIndex = partQuestions.findIndex((q) => q.node.id === question.node.id);
  const partIndex = isViewReport ? 0 : watch('parts')?.findIndex((p) => p.partId === activePart?.node.id);

  const { handleSubmit: listeningHandleSubmit } = useIeltsListening();
  const { handleSubmit: readingHandleSubmit } = useIeltsReading();
  const { handleSubmitPracticeIeltsPart } = useDoIeltsWriting();
  const handleSubmit =
    type === IeltsSkill.Listening ? listeningHandleSubmit : type === IeltsSkill.Writing ? handleSubmitPracticeIeltsPart : readingHandleSubmit;

  const handleSubmitDetail = useCallback(
    async (text: string, detailId: string) => {
      if (isViewReport) return;

      await handleSubmit({
        questionId: question.node.id,
        detailAnswer: {
          detailId,
          text,
        },
      });
    },
    [handleSubmit, isViewReport, question.node.id]
  );

  if (partIndex === -1) return null;

  const insertionContent = parse(question.node.contentHtml, {
    replace: (domNode) => {
      if (domNode.type === 'tag' && domNode.name === 'blank') {
        const detail = details.find((d) => d.id === domNode.attribs.id);
        if (!detail) return domNode;

        const detailAnswers = isViewReport ? [] : watch(`parts.${partIndex}.questions.${questionIndex || 0}.detailAnswers`);
        const detailAttempt = questionAttempt?.detailAnswers.find((d) => d.detailId === detail.id);
        const correctAnswers = detail.options?.filter((o) => o.correctAnswer);

        const defaultValue = (isViewReport ? detailAttempt?.text : detailAnswers?.find((d) => d.detailId === detail.id)?.text) || '';

        return (
          <span key={detail.id} className="inline-flex items-center flex-wrap gap-1">
            <IeltsInput
              id={detail.id}
              className="my-1 placeholder:text-xs placeholder:italic placeholder:text-[#637381] placeholder:font-bold"
              readOnly={isViewReport}
              placeholder={
                isViewReport
                  ? t('listening_reading.no_answer')
                  : // t('listening_reading.input_answer')
                    ''
              }
              result={
                isViewReport
                  ? Boolean(
                      detailAttempt &&
                        correctAnswers?.some(
                          (a) =>
                            a.text?.toLowerCase().trim() ===
                            detailAttempt.text?.toLowerCase().trim(),
                        ),
                    )
                  : undefined
              }
              defaultValue={defaultValue}
              onBlur={(e) => {
                void handleSubmitDetail(e.target.value, detail.id);
              }}
            />
          </span>
        );
      }
      return configQuestionContent(domNode, theme);
    },
  });

  return (
    <div>
      <p
        className="text-lg max-md:text-base font-bold mb-[5px]"
        style={{
          color: 'var(--ielts-accent)',
        }}
      >
        {question.node.title}
      </p>
      <div className="">
        <div className="max-xl:max-w-none">
          <div className="mb-4 max-md:mb-2">{insertionContent}</div>
        </div>
      </div>
    </div>
  );
}
